/**
 * A read bound to the session it was issued under.
 *
 * Every async write into account-scoped state has the same hazard: the request
 * goes out for one session, the answer comes back after that session ended (a
 * sign-out, or a sign-in as someone else), and the write lands on whoever is
 * signed in by then. #940 found it first on the capability map, then on the
 * plan fields, the whole user (/me, PUT /profile), the push and avatar writers,
 * patchUser's server-confirmed writes, and the api interceptor's 401 and ban
 * handling.
 *
 * The fix is one rule: take a ticket when the request is ISSUED, and write only
 * if `isCurrentSession(ticket)` still holds when the answer lands. The session
 * is the bearer token, which every sign-in mints anew and every sign-out
 * clears, so no auth setter (present or future, helper or bare set()) has to
 * remember to advance anything. `seq` additionally orders reads of one field
 * by issue time.
 */
export interface SessionRead {
  readonly seq: number;
  readonly session: string | null;
}

let issued = 0;

let currentSession: () => string | null = () => null;

/** Registered once, by the auth store, which owns the session token. */
export function setSessionProvider(provider: () => string | null): void {
  currentSession = provider;
}

/**
 * Ticket for a request whose answer will be written somewhere. Take it at ISSUE.
 *
 * `session` names the session explicitly, for a request that goes out under a
 * session that has ALREADY ended on screen: the leave a sign-out or a switch
 * sends with the outgoing account's token (api.ts `sessionToken`). Its answer
 * then belongs to that ended session, so nothing reacts to it for the new one.
 */
export function issueRead(session: string | null = currentSession()): SessionRead {
  issued += 1;
  return { seq: issued, session };
}

/**
 * Is the session this read went out under still the session on screen? An
 * answer that fails this belongs to another account (or to a signed-out one)
 * and must write nothing.
 */
export function isCurrentSession(read: SessionRead): boolean {
  return read.session === currentSession();
}

/**
 * THE primitive for session-bound async work (#940).
 *
 * Takes the ticket, starts `work` under it, and settles to ONE outcome:
 *   'ok'     the work resolved and its session is still the one on screen;
 *   'failed' the work threw or rejected, and its session is still current;
 *   'ended'  the session changed while the work was out, whatever the work did
 *            (resolved, rejected, threw). Nothing may be written, shown or
 *            started for an 'ended' outcome.
 * The caller acts on 'ok' (and reports 'failed') SYNCHRONOUSLY after the await:
 * any further await is new async work and goes through withSession again.
 * Six review rounds found one hand-written branch missing at a time (a reject
 * path here, a toast there); a caller of this primitive cannot reach its
 * success or failure code for another session's answer.
 */
export type SessionOutcome<T> =
  | { readonly status: 'ok'; readonly value: T; readonly read: SessionRead }
  | { readonly status: 'failed'; readonly error: unknown; readonly read: SessionRead }
  | { readonly status: 'ended'; readonly read: SessionRead };

/**
 * A user-initiated flow (purchase, restore, cancel, delete, consent, avatar,
 * agreement, ...) captures its session at the START, before its first await,
 * and every later step runs under that capture (Codex r7 on #940: restore()
 * took its capture only after the native store had answered, so a switch
 * during the store dialog let A's restore report success for B).
 *
 * `step(work)` settles like withSession, but always against the flow's
 * ORIGINAL capture: once the session that began the flow has ended, every
 * step comes back 'ended', whatever it did.
 */
export interface SessionFlow {
  readonly read: SessionRead;
  isCurrent(): boolean;
  step<T>(work: () => Promise<T> | T): Promise<SessionOutcome<T>>;
}

export function beginSessionFlow(): SessionFlow {
  const read = issueRead();
  return {
    read,
    isCurrent: () => isCurrentSession(read),
    async step<T>(work: () => Promise<T> | T): Promise<SessionOutcome<T>> {
      if (!isCurrentSession(read)) return { status: 'ended', read };
      try {
        const value = await work();
        if (!isCurrentSession(read)) return { status: 'ended', read };
        return { status: 'ok', value, read };
      } catch (error) {
        if (!isCurrentSession(read)) return { status: 'ended', read };
        return { status: 'failed', error, read };
      }
    },
  };
}

/** One step of session-bound work: a flow that begins and ends in one await. */
export function withSession<T>(work: () => Promise<T> | T): Promise<SessionOutcome<T>> {
  return beginSessionFlow().step(work);
}

/**
 * Same-session ordering for reads that feed one piece of shared state (Codex
 * r7 on #940: the plan read ordered its SUCCESSES only, so an older read that
 * failed after a newer one succeeded still wrote its error and cleared the
 * loading flag). A lane hands out issue-ordered tickets; withOrderedSession
 * settles every outcome of a read (resolve, reject, throw) as 'superseded'
 * once a newer read in the same lane was issued, so only the newest read
 * ever touches the data, the error field or the loading flag. Session changes
 * still win over everything ('ended').
 */
export interface ReadLane {
  /** Ticket for a new read; any read issued before it is superseded. */
  next(): number;
  /** Supersede every read in flight without issuing one (a confirmed write). */
  supersede(): void;
  /** Is `ticket` still the newest read issued in this lane? */
  isNewest(ticket: number): boolean;
}

export function createReadLane(): ReadLane {
  let newest = 0;
  return {
    next: () => {
      newest += 1;
      return newest;
    },
    supersede: () => {
      newest += 1;
    },
    isNewest: (ticket) => ticket === newest,
  };
}

export type OrderedOutcome<T> =
  | SessionOutcome<T>
  | {
      readonly status: 'superseded';
      readonly settled: 'resolved' | 'rejected';
      readonly read: SessionRead;
    };

export async function withOrderedSession<T>(
  lane: ReadLane,
  work: () => Promise<T> | T,
): Promise<OrderedOutcome<T>> {
  const ticket = lane.next();
  const outcome = await withSession(work);
  if (outcome.status === 'ended') return outcome;
  if (!lane.isNewest(ticket)) {
    return {
      status: 'superseded',
      settled: outcome.status === 'ok' ? 'resolved' : 'rejected',
      read: outcome.read,
    };
  }
  return outcome;
}

/**
 * THE ownership primitive for flows that hold a sheet, a resolver or a
 * scheduled continuation (#940).
 *
 * `open()` starts an invocation and returns its token. A completion captures
 * the token before its await and acts only if `owns(token)` still holds: the
 * flow has not been closed or re-opened by anyone since, AND the session that
 * opened it is still current. Reading the flow's CURRENT state after an await
 * without this check is how A's in-flight Continue closed B's newer sheet and
 * accepted B's request (Codex r6).
 */
export interface FlowToken {
  readonly id: number;
  readonly read: SessionRead;
}

export interface Flow {
  /** Start a new invocation; any previous token stops owning the flow. */
  open(): FlowToken;
  /** The token of the invocation that owns the flow now, if any. */
  current(): FlowToken | null;
  /** Does `token` still own the flow, for a session that is still current? */
  owns(token: FlowToken | null | undefined): boolean;
  /** End the current invocation; nothing captured before this owns the flow. */
  close(): void;
}

export function createFlow(): Flow {
  let owner: FlowToken | null = null;
  let nextId = 0;
  return {
    open() {
      nextId += 1;
      owner = { id: nextId, read: issueRead() };
      return owner;
    },
    current: () => owner,
    owns: (token) => token != null && token === owner && isCurrentSession(token.read),
    close() {
      owner = null;
    },
  };
}

/**
 * Per-user stores reset themselves, SYNCHRONOUSLY, on every session change and
 * on entering the guest tour (Codex r9 on #940). Each store registers its own
 * reset when its module loads; a store that never loaded holds no data (none of
 * them persists user data). This replaced a fire-and-forget reset over dynamic
 * imports, which a slow chunk let land after the NEW account's freshly loaded
 * data and wipe it. Run at the switch itself, the reset can only ever precede
 * the new session's writes.
 */
const userScopedResets = new Set<() => void>();

export function registerUserScopedReset(reset: () => void): () => void {
  userScopedResets.add(reset);
  return () => userScopedResets.delete(reset);
}

export function resetUserScopedStores(): void {
  for (const reset of userScopedResets) {
    try {
      reset();
    } catch (error) {
      console.error('A per-user store reset failed:', error);
    }
  }
}

type SessionChangeListener = (next: string | null, previous: string | null) => void;

const sessionChangeListeners = new Set<SessionChangeListener>();

/**
 * Register what must happen to a UI flow when the session changes: a sheet,
 * prompt or parked request opened on behalf of one session must not outlive
 * it, or the next account's click acts for the previous one (#940: A's circle
 * agreement, still up, was recorded for B on Continue). Each flow's store
 * registers its own dismissal at load; a store that never loaded has nothing
 * open. Returns an unregister function.
 */
export function onSessionChange(listener: SessionChangeListener): () => void {
  sessionChangeListeners.add(listener);
  return () => sessionChangeListeners.delete(listener);
}

/** Called by the auth store's session-change path on every token change. */
export function notifySessionChange(next: string | null, previous: string | null): void {
  for (const listener of sessionChangeListeners) {
    try {
      listener(next, previous);
    } catch (error) {
      console.error('A session-change listener failed:', error);
    }
  }
}
